<template>
  <Drawer :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <div class="modal-header">
      <div>
        <h2 class="modal-title">{{ suppliesOnly ? 'Corrigir insumos' : 'Realizar atendimento' }}</h2>
        <p class="wizard-subtitle">Passo {{ step }} de {{ totalSteps }}</p>
      </div>
      <button type="button" class="modal-close" aria-label="Fechar" @click="handleClose">
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="wizard-steps" role="tablist" aria-label="Etapas">
      <template v-for="(item, index) in wizardStepItems" :key="item.id">
        <div
          class="wizard-step-item"
          :class="{ active: step === item.step, done: step > item.step }"
          role="tab"
          :aria-selected="step === item.step"
        >
          <span class="wizard-step-marker" aria-hidden="true">
            <i v-if="step > item.step" class="material-icons">check</i>
            <span v-else>{{ item.number }}</span>
          </span>
          <span class="wizard-step-label">{{ item.label }}</span>
        </div>
        <span
          v-if="index < wizardStepItems.length - 1"
          class="wizard-step-connector"
          :class="{ done: step > item.step }"
          aria-hidden="true"
        />
      </template>
    </div>

    <div class="modal-body">
      <!-- Step 1: Payment -->
      <section v-if="step === paymentStepNumber">
        <AppointmentPaymentFields
          :paid="paidLocal"
          :payment-method="paymentMethodLocal"
          :all-payment-methods="allPaymentMethods"
          :total="total"
          :default-fee-percent="defaultFeePercent"
          @update:paid="paidLocal = $event"
          @update:payment-method="paymentMethodLocal = $event"
        />
      </section>

      <!-- Step 2: Supplies -->
      <section v-else-if="step === suppliesStepNumber && showSuppliesStep" class="supplies-step">
        <p v-if="supplyValidationError" class="field-error" role="alert">{{ supplyValidationError }}</p>
        <p v-if="serviceBlocks.length === 0" class="empty-hint">
          Nenhum insumo configurado para os serviços deste atendimento.
        </p>
        <div
          v-for="block in serviceBlocks"
          :key="block.serviceId"
          class="supply-service-block"
        >
          <h3 class="section-title supply-service-title">{{ block.serviceName }}</h3>

          <div v-for="line in block.quantityLines" :key="'q-' + line.supplyId" class="supply-row">
            <label>{{ supplyName(line.supplyId) }}</label>
            <input
              v-model.number="line.quantity"
              type="number"
              min="0"
              step="1"
              class="supply-qty-input"
            />
          </div>

          <div
            v-for="group in block.containerGroups"
            :key="group.groupId"
            class="supply-group"
          >
            <h4 class="supply-group-title">{{ group.groupLabel || 'Insumos' }}</h4>
            <div
              v-for="supplyId in group.allowedSupplyIds"
              :key="supplyId"
              class="container-supply-block"
              :class="{ 'container-supply-block--selected': block.containerSelected[supplyId] }"
            >
              <label class="container-supply-header checkbox-label">
                <input
                  type="checkbox"
                  class="container-supply-checkbox"
                  :checked="block.containerSelected[supplyId]"
                  @change="toggleContainer(block, supplyId, $event.target.checked)"
                />
                <span class="container-supply-name">{{ supplyName(supplyId) }}</span>
                <span class="container-supply-stock">
                  {{ openCountLabel(supplyById(supplyId), openCount(supplyId)) }}
                </span>
              </label>
              <div v-if="block.containerSelected[supplyId]" class="container-supply-controls">
                <div class="counter-row">
                  <span class="counter-label">{{ openActionLabel(supplyById(supplyId)) }}</span>
                  <div class="counter">
                    <button type="button" class="btn-icon-sm" @click="adjustOpen(block, supplyId, -1)">−</button>
                    <span class="counter-value">{{ block.containerEvents[supplyId]?.opened || 0 }}</span>
                    <button type="button" class="btn-icon-sm" @click="adjustOpen(block, supplyId, 1)">+</button>
                  </div>
                </div>
                <div class="counter-row">
                  <span class="counter-label">{{ depletedLabel(supplyById(supplyId)) }}</span>
                  <div class="counter">
                    <button type="button" class="btn-icon-sm" @click="adjustDeplete(block, supplyId, -1)">−</button>
                    <span class="counter-value">{{ block.containerEvents[supplyId]?.depleted || 0 }}</span>
                    <button type="button" class="btn-icon-sm" @click="adjustDeplete(block, supplyId, 1)">+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      <!-- Confirm -->
      <section v-else-if="step === confirmStep">
        <h3 class="section-title">Resumo</h3>
        <p v-if="!suppliesOnly" class="summary-line">
          <strong>Pagamento:</strong>
          {{ paidLocal != null ? Number(paidLocal).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : '—' }}
        </p>
        <template v-if="showSuppliesStep">
          <ul class="summary-list">
            <li v-for="(line, idx) in summaryUsage" :key="idx">
              {{ supplyName(line.supplyId) }}
              <template v-if="line.kind === 'quantity'"> — {{ line.quantity }} {{ unitLabel(line.supplyId) }}</template>
              <template v-else> — utilizado</template>
            </li>
          </ul>
          <ul v-if="summaryEvents.length" class="summary-list summary-list--events">
            <li v-for="(ev, idx) in summaryEvents" :key="'ev-' + idx">
              {{ supplyName(ev.supplyId) }}:
              <span v-if="ev.opened"> abriu {{ ev.opened }}</span>
              <span v-if="ev.depleted"> esgotou {{ ev.depleted }}</span>
            </li>
          </ul>
        </template>
      </section>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" :disabled="saving" @click="handleBack">
        {{ step === 1 ? 'Cancelar' : 'Voltar' }}
      </button>
      <button
        v-if="step < confirmStep"
        type="button"
        class="btn btn-primary"
        @click="handleNext"
      >
        Continuar
      </button>
      <button
        v-else
        type="button"
        class="btn btn-primary"
        :disabled="saving"
        @click="handleConfirm"
      >
        {{ saving ? 'Salvando...' : 'Confirmar' }}
      </button>
    </div>

    <ConfirmDialog
      v-model="showOpenPrompt"
      :title="openPromptTitle"
      :message="openPromptMessage"
      confirm-label="Abrir"
      cancel-label="Voltar"
      @confirm="confirmOpenPrompt"
      @cancel="cancelOpenPrompt"
    />
  </Drawer>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import AppointmentPaymentFields from '@/components/AppointmentPaymentFields.vue';
import { useSupplyStore } from '@/stores/supply';
import { useAppointmentStore } from '@/stores/appointment';
import { useServiceStore } from '@/stores/service';
import { getLastVisitSupplies } from '@/composables/useLastVisitSupplies';
import { hasSupplyPayloadActivity } from '@/utils/inventoryActivity';
import {
  appointmentNeedsSuppliesStep,
  buildServiceSupplyState,
  buildServiceSupplyStateFromAppointment,
  buildServiceSupplyStateFromUsageOnly,
  flattenSupplyPayload,
  resolveAllowedSupplyIds,
  sortSupplyIdsByName,
} from '@/utils/supplyConfig';
import {
  openActionLabel,
  depletedLabel,
  openCountLabel,
} from '@/utils/supplyLabels';

export default {
  name: 'CloseAppointmentWizard',
  components: { Drawer, ConfirmDialog, AppointmentPaymentFields },
  props: {
    modelValue: { type: Boolean, default: false },
    clientId: { type: String, default: '' },
    selectedServices: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    allPaymentMethods: { type: Array, default: () => [] },
    defaultFeePercent: { type: Number, default: 0 },
    initialPaid: { type: [Number, String], default: null },
    initialPaymentMethod: { type: Object, default: null },
    saving: { type: Boolean, default: false },
    suppliesOnly: { type: Boolean, default: false },
    initialSupplyUsage: { type: Array, default: () => [] },
    initialContainerEvents: { type: Array, default: () => [] },
    inventoryAlreadyApplied: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  data() {
    return {
      step: 1,
      paidLocal: null,
      paymentMethodLocal: null,
      serviceStates: {},
      showOpenPrompt: false,
      openPromptSupplyId: null,
      openPromptBlock: null,
      pendingNextAfterPrompt: false,
      supplyValidationError: '',
    };
  },
  computed: {
    supplyStore() {
      return useSupplyStore();
    },
    serviceStore() {
      return useServiceStore();
    },
    appointmentStore() {
      return useAppointmentStore();
    },
    hasSuppliesStep() {
      return appointmentNeedsSuppliesStep(
        this.selectedServices,
        this.serviceStore.services
      );
    },
    hasExistingSupplyData() {
      if (!this.suppliesOnly) return false;
      return hasSupplyPayloadActivity(
        this.initialSupplyUsage,
        this.initialContainerEvents,
      );
    },
    showSuppliesStep() {
      if (this.suppliesOnly) {
        return this.hasSuppliesStep || this.hasExistingSupplyData;
      }
      return this.hasSuppliesStep;
    },
    paymentStepNumber() {
      return this.suppliesOnly ? -1 : 1;
    },
    suppliesStepNumber() {
      return this.suppliesOnly ? 1 : 2;
    },
    totalSteps() {
      if (this.suppliesOnly) {
        return this.showSuppliesStep ? 2 : 1;
      }
      return this.hasSuppliesStep ? 3 : 2;
    },
    confirmStep() {
      return this.totalSteps;
    },
    wizardStepItems() {
      if (this.suppliesOnly) {
        const items = [];
        if (this.showSuppliesStep) {
          items.push({ id: 'supplies', step: 1, number: 1, label: 'Insumos' });
        }
        items.push({
          id: 'confirm',
          step: this.confirmStep,
          number: items.length + 1,
          label: 'Confirmar',
        });
        return items;
      }
      const items = [{ id: 'payment', step: 1, number: 1, label: 'Pagamento' }];
      if (this.hasSuppliesStep) {
        items.push({ id: 'supplies', step: 2, number: 2, label: 'Insumos' });
      }
      items.push({
        id: 'confirm',
        step: this.confirmStep,
        number: items.length + 1,
        label: 'Confirmar',
      });
      return items;
    },
    serviceBlocks() {
      const supplies = this.supplyStore.supplies;
      return Object.values(this.serviceStates).map((block) => ({
        ...block,
        containerGroups: (block.containerGroups || []).map((group) => ({
          ...group,
          allowedSupplyIds: sortSupplyIdsByName(group.allowedSupplyIds || [], supplies),
        })),
      }));
    },
    summaryUsage() {
      return flattenSupplyPayload(this.serviceStates).supplyUsage;
    },
    summaryEvents() {
      return flattenSupplyPayload(this.serviceStates).containerEvents;
    },
    openPromptTitle() {
      const s = this.openPromptSupplyId ? this.supplyById(this.openPromptSupplyId) : null;
      return s ? openActionLabel(s) : 'Abrir';
    },
    openPromptMessage() {
      const s = this.openPromptSupplyId ? this.supplyById(this.openPromptSupplyId) : null;
      const name = s?.name || 'insumo';
      return `Não há ${s?.unit || 'unidade'} de ${name} aberto(a). Deseja abrir agora?`;
    },
    quantityCreditBySupplyId() {
      const map = new Map();
      for (const line of this.initialSupplyUsage || []) {
        if (line?.kind !== 'quantity' || !line.supplyId) continue;
        const qty = Math.max(0, Math.floor(Number(line.quantity) || 0));
        if (qty <= 0) continue;
        map.set(line.supplyId, (map.get(line.supplyId) || 0) + qty);
      }
      return map;
    },
    containerCreditBySupplyId() {
      const map = {};
      for (const ev of this.initialContainerEvents || []) {
        if (!ev?.supplyId) continue;
        map[ev.supplyId] = {
          opened: Math.max(0, Math.floor(Number(ev.opened) || 0)),
          depleted: Math.max(0, Math.floor(Number(ev.depleted) || 0)),
        };
      }
      return map;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetWizard();
    },
    selectedServices: {
      handler() {
        if (this.modelValue) this.initServiceStates();
      },
      deep: true,
    },
  },
  methods: {
    openActionLabel,
    depletedLabel,
    openCountLabel,
    supplyById(id) {
      return this.supplyStore.supplyById(id);
    },
    supplyName(id) {
      return this.supplyById(id)?.name || id;
    },
    unitLabel(id) {
      const s = this.supplyById(id);
      return s?.unitPlural || s?.unit || '';
    },
    openCount(supplyId) {
      const base = this.supplyStore.openCountBySupplyId[supplyId] || 0;
      if (!this.inventoryAlreadyApplied) return base;
      const credit = this.containerCreditBySupplyId[supplyId];
      return base + (credit?.depleted || 0);
    },
    effectiveSealedCount(supply) {
      const sealed = Number(supply?.sealedCount) || 0;
      if (!this.inventoryAlreadyApplied || !supply?.id) return sealed;
      const credit = this.containerCreditBySupplyId[supply.id];
      return sealed + (credit?.opened || 0);
    },
    resetWizard() {
      this.step = 1;
      this.supplyValidationError = '';
      this.paidLocal = this.initialPaid != null && this.initialPaid !== ''
        ? Number(this.initialPaid)
        : this.total;
      this.paymentMethodLocal = this.initialPaymentMethod;
      this.initServiceStates();
    },
    initServiceStates() {
      const states = {};
      const normalizedUsage = this.normalizeInitialSupplyUsage();
      for (const sel of this.selectedServices || []) {
        const serviceId = sel.service;
        const service = this.serviceStore.services[serviceId];
        const hasConfig = service &&
          appointmentNeedsSuppliesStep([sel], this.serviceStore.services);
        const hasRecordedUsage = normalizedUsage.some(
          (line) => line.serviceId === serviceId,
        );
        const hasRecordedContainers = this.serviceHasRecordedContainers(
          serviceId,
          normalizedUsage,
          service,
        );

        if (this.suppliesOnly) {
          if (!hasConfig && !hasRecordedUsage && !hasRecordedContainers) continue;
        } else if (!service || !hasConfig) {
          continue;
        }

        const lastVisit = this.suppliesOnly
          ? []
          : getLastVisitSupplies(
            this.appointmentStore,
            this.clientId,
            serviceId,
          );

        let built;
        let containerGroups;
        if (hasConfig) {
          built = this.suppliesOnly
            ? buildServiceSupplyStateFromAppointment(
              { ...service, id: serviceId },
              normalizedUsage,
              this.initialContainerEvents,
              this.supplyStore.supplies,
            )
            : buildServiceSupplyState(
              { ...service, id: serviceId },
              lastVisit,
              this.supplyStore.supplies,
            );
          containerGroups = (service.supplyConfig?.containers || []).map((group) => ({
            ...group,
            allowedSupplyIds: resolveAllowedSupplyIds(group, this.supplyStore.supplies),
          }));
        } else {
          built = buildServiceSupplyStateFromUsageOnly(
            serviceId,
            normalizedUsage,
            this.initialContainerEvents,
            this.supplyStore.supplies,
          );
          containerGroups = built.containerGroups || [];
        }

        states[serviceId] = {
          serviceId,
          serviceName: service?.name || 'Serviço',
          quantityLines: built.quantityLines,
          containerSelected: built.containerSelected,
          containerEvents: built.containerEvents,
          containerGroups,
        };
      }
      this.serviceStates = states;
    },
    normalizeInitialSupplyUsage() {
      const usage = [...(this.initialSupplyUsage || [])];
      const serviceIds = (this.selectedServices || []).map((sel) => sel.service).filter(Boolean);
      if (serviceIds.length === 0) return usage;
      const defaultServiceId = serviceIds[0];
      return usage.map((line) => (
        line?.serviceId ? line : { ...line, serviceId: defaultServiceId }
      ));
    },
    serviceHasRecordedContainers(serviceId, supplyUsage, service) {
      const events = this.initialContainerEvents || [];
      if (!events.length) return false;
      const supplyIds = new Set();
      for (const line of supplyUsage) {
        if (line.serviceId === serviceId && line.supplyId) {
          supplyIds.add(line.supplyId);
        }
      }
      for (const group of service?.supplyConfig?.containers || []) {
        for (const id of resolveAllowedSupplyIds(group, this.supplyStore.supplies)) {
          supplyIds.add(id);
        }
      }
      return events.some((ev) => ev?.supplyId && supplyIds.has(ev.supplyId));
    },
    toggleContainer(block, supplyId, checked) {
      block.containerSelected[supplyId] = checked;
      if (!block.containerEvents[supplyId]) {
        block.containerEvents[supplyId] = { opened: 0, depleted: 0 };
      }
    },
    adjustOpen(block, supplyId, delta) {
      const supply = this.supplyById(supplyId);
      if (!supply) return;
      const ev = block.containerEvents[supplyId] || { opened: 0, depleted: 0 };
      const next = Math.max(0, (ev.opened || 0) + delta);
      if (delta > 0) {
        const sealed = this.effectiveSealedCount(supply);
        const alreadyOpening = next;
        if (alreadyOpening > sealed) {
          return;
        }
      }
      ev.opened = next;
      block.containerEvents[supplyId] = ev;
    },
    adjustDeplete(block, supplyId, delta) {
      const ev = block.containerEvents[supplyId] || { opened: 0, depleted: 0 };
      const opened = ev.opened || 0;
      const maxDeplete = this.openCount(supplyId) + opened;
      const next = Math.max(0, Math.min(maxDeplete, (ev.depleted || 0) + delta));
      ev.depleted = next;
      block.containerEvents[supplyId] = ev;
    },
    handleClose() {
      this.$emit('update:modelValue', false);
      this.$emit('cancel');
    },
    handleBack() {
      if (this.step === 1) {
        this.handleClose();
        return;
      }
      if (this.step === this.confirmStep) {
        this.step = this.showSuppliesStep ? this.suppliesStepNumber : 1;
        return;
      }
      this.step = 1;
    },
    handleNext() {
      if (!this.suppliesOnly && this.step === 1) {
        if (this.paidLocal === null || this.paidLocal === '' || Number.isNaN(Number(this.paidLocal))) {
          return;
        }
        if (!this.showSuppliesStep) {
          this.step = this.confirmStep;
          return;
        }
        this.step = this.suppliesStepNumber;
        return;
      }
      if (this.step === this.suppliesStepNumber && this.showSuppliesStep) {
        this.supplyValidationError = '';
        const issue = this.validateSupplies();
        if (issue) return;
        this.step = this.confirmStep;
      }
    },
    validateSupplies() {
      const quantityBySupplyId = new Map();
      for (const block of this.serviceBlocks) {
        for (const line of block.quantityLines || []) {
          if (!line?.supplyId) continue;
          const qty = Math.max(0, Math.floor(Number(line.quantity) || 0));
          if (qty <= 0) continue;
          quantityBySupplyId.set(
            line.supplyId,
            (quantityBySupplyId.get(line.supplyId) || 0) + qty,
          );
        }
      }
      for (const [supplyId, requested] of quantityBySupplyId) {
        const supply = this.supplyById(supplyId);
        if (!supply || supply.tracking !== 'quantity') continue;
        const onHand = Number(supply.quantityOnHand) || 0;
        const credit = this.inventoryAlreadyApplied
          ? (this.quantityCreditBySupplyId.get(supplyId) || 0)
          : 0;
        if (requested > onHand + credit) {
          const unit = supply.unitPlural || supply.unit || 'un';
          this.supplyValidationError =
            `Estoque insuficiente de ${supply.name}: há ${onHand + credit} ${unit}, mas foram informados ${requested}.`;
          return true;
        }
      }

      for (const block of this.serviceBlocks) {
        for (const [supplyId, selected] of Object.entries(block.containerSelected || {})) {
          if (!selected) continue;
          const supply = this.supplyById(supplyId);
          if (!supply || supply.tracking !== 'container') continue;
          const ev = block.containerEvents[supplyId] || { opened: 0, depleted: 0 };
          const openCount = this.openCount(supplyId);
          const opened = ev.opened || 0;
          const depleted = ev.depleted || 0;
          const availableToDeplete = openCount + opened;

          if (depleted > availableToDeplete) {
            return true;
          }

          // No open stock and user did not open or deplete anything in this flow.
          if (openCount === 0 && opened === 0 && depleted === 0) {
            this.openPromptSupplyId = supplyId;
            this.openPromptBlock = block;
            this.pendingNextAfterPrompt = true;
            this.showOpenPrompt = true;
            return true;
          }

          if (opened > 0) {
            const sealed = this.effectiveSealedCount(supply);
            if (opened > sealed) {
              return true;
            }
          }
        }
      }
      return false;
    },
    confirmOpenPrompt() {
      if (this.openPromptBlock && this.openPromptSupplyId) {
        const ev = this.openPromptBlock.containerEvents[this.openPromptSupplyId] ||
          { opened: 0, depleted: 0 };
        ev.opened = (ev.opened || 0) + 1;
        this.openPromptBlock.containerEvents[this.openPromptSupplyId] = ev;
      }
      this.showOpenPrompt = false;
      this.openPromptSupplyId = null;
      this.openPromptBlock = null;
      if (this.pendingNextAfterPrompt) {
        this.pendingNextAfterPrompt = false;
        this.step = this.confirmStep;
      }
    },
    cancelOpenPrompt() {
      this.showOpenPrompt = false;
      this.pendingNextAfterPrompt = false;
      this.openPromptSupplyId = null;
      this.openPromptBlock = null;
    },
    handleConfirm() {
      if (this.suppliesOnly) {
        const flat = flattenSupplyPayload(this.serviceStates);
        this.$emit('confirm', {
          supplyUsage: flat.supplyUsage,
          containerEvents: flat.containerEvents,
        });
        return;
      }
      const payload = {
        paid: this.paidLocal,
        paymentMethod: this.paymentMethodLocal,
        suppliesSkipped: false,
      };
      if (this.hasSuppliesStep) {
        const flat = flattenSupplyPayload(this.serviceStates);
        payload.supplyUsage = flat.supplyUsage;
        payload.containerEvents = flat.containerEvents;
      } else {
        payload.supplyUsage = [];
        payload.containerEvents = [];
      }
      this.$emit('confirm', payload);
    },
  },
};
</script>

<style scoped>
.wizard-subtitle {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}

.wizard-steps {
  display: flex;
  align-items: center;
  gap: 0;
  padding: var(--space-md) var(--space-lg) var(--space-lg);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg);
}

.wizard-step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-xs);
  min-width: 4.5rem;
  text-align: center;
}

.wizard-step-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.wizard-step-marker .material-icons {
  font-size: 1rem;
}

.wizard-step-label {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  line-height: 1.2;
  transition: color 0.2s;
}

.wizard-step-item.active .wizard-step-marker {
  border-color: var(--color-accent);
  background: var(--color-accent);
  color: #fff;
}

.wizard-step-item.active .wizard-step-label {
  color: var(--color-accent);
  font-weight: 600;
}

.wizard-step-item.done .wizard-step-marker {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.wizard-step-item.done .wizard-step-label {
  color: var(--color-text-secondary);
}

.wizard-step-connector {
  flex: 1;
  height: 2px;
  margin: 0 var(--space-xs);
  margin-bottom: 1.25rem;
  background: var(--color-border);
  border-radius: 1px;
  transition: background 0.2s;
}

.wizard-step-connector.done {
  background: var(--color-accent);
}

.supply-service-block {
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);
}

.supply-service-title {
  margin-bottom: var(--space-md);
}

.supply-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  margin-bottom: var(--space-sm);
}

.supply-qty-input {
  width: 5rem;
}

.supply-group-title {
  font-size: 0.9rem;
  margin: var(--space-md) 0 var(--space-sm);
}

.container-supply-block {
  margin-bottom: var(--space-sm);
  padding: var(--space-md);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  transition: border-color 0.2s, background 0.2s;
}

.container-supply-block--selected {
  border-color: rgba(176, 125, 125, 0.4);
  background: var(--color-accent-soft);
}

.container-supply-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  width: 100%;
  margin: 0;
  cursor: pointer;
  font-weight: 600;
  text-transform: none;
}

.container-supply-checkbox {
  width: 1.05rem;
  height: 1.05rem;
  accent-color: var(--color-accent);
  flex-shrink: 0;
}

.container-supply-name {
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
  color: var(--color-text);
}

.container-supply-stock {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-align: right;
}

.container-supply-controls {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.container-supply-block--selected .container-supply-controls {
  border-top-color: rgba(176, 125, 125, 0.2);
}

.counter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
}

.counter-label {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.counter {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}

.counter-value {
  min-width: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text);
}

.btn-icon-sm {
  width: 28px;
  height: 28px;
  border-radius: calc(var(--radius-sm) - 2px);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: background 0.15s, color 0.15s;
}

.btn-icon-sm:hover {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.summary-line {
  margin-bottom: var(--space-md);
}

.summary-list {
  margin: 0;
  padding-left: var(--space-lg);
}

.summary-list--events {
  margin-top: var(--space-md);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.helper-text {
  margin-bottom: var(--space-md);
  color: var(--color-text-secondary);
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin: 0 0 var(--space-md);
}

.empty-hint {
  margin: 0;
  text-align: center;
  color: var(--color-text-muted);
}
</style>
